import { useCallback, useEffect, useMemo, useState } from 'react'
import { supabase } from '../supabase'
import { useUser } from '../UserContext'

export function useCrmReferenceData() {
  const {
    user,
    orgId,
    isAdmin,
    activeCrmWorkspace,
    activeCrmWorkspaceId,
    activeCrmMembership,
    can
  } = useUser()
  const [businesses, setBusinesses] = useState([])
  const [profiles, setProfiles] = useState([])
  const [loadingReferences, setLoadingReferences] = useState(true)
  const workspaceOrgId = activeCrmWorkspace?.org_id || orgId

  const refreshReferences = useCallback(async () => {
    if (!activeCrmWorkspaceId) {
      setBusinesses([])
      setProfiles([])
      setLoadingReferences(false)
      return
    }

    setLoadingReferences(true)
    const [{ data: businessData, error: businessError }, { data: profileData, error: profileError }] = await Promise.all([
      supabase
        .from('crm_businesses')
        .select('id, workspace_id, name, archived')
        .eq('workspace_id', activeCrmWorkspaceId)
        .eq('archived', false)
        .order('name'),
      supabase
        .from('profiles')
        .select('id, email, full_name')
        .eq('org_id', workspaceOrgId)
        .order('full_name')
    ])

    if (businessError) console.error('Failed to load CRM Businesses:', businessError)
    if (profileError) console.error('Failed to load CRM owners:', profileError)
    setBusinesses(businessData || [])
    setProfiles(profileData || [])
    setLoadingReferences(false)
  }, [activeCrmWorkspaceId, workspaceOrgId])

  useEffect(() => {
    refreshReferences()
  }, [refreshReferences])

  const canManageRecords = can('crm.manage')
  const canManageWorkspace = can('crm.settings.manage') && (isAdmin || activeCrmMembership?.role === 'workspace_admin')
  const currentOwner = useMemo(
    () => profiles.find(profile => profile.id === user?.id) || null,
    [profiles, user?.id]
  )

  return {
    user,
    orgId,
    activeCrmWorkspace,
    activeCrmWorkspaceId,
    businesses,
    profiles,
    currentOwner,
    canManageWorkspace,
    canManageRecords,
    loadingReferences,
    refreshReferences
  }
}

export function CrmSetupNotice({ hasWorkspace, hasBusinesses }) {
  if (!hasWorkspace) {
    return (
      <div className="hv-table-wrap">
        <div className="hv-empty">
          <div className="hv-empty-title">No CRM workspace available</div>
          <div className="hv-empty-text">Ask an organisation administrator to add you to a CRM workspace.</div>
        </div>
      </div>
    )
  }

  if (!hasBusinesses) {
    return (
      <div className="hv-table-wrap">
        <div className="hv-empty">
          <div className="hv-empty-title">Add a Business first</div>
          <div className="hv-empty-text">CRM records must belong to a Business. Create one from the Companies page.</div>
        </div>
      </div>
    )
  }

  return null
}
